/* CRO cart drawer behaviors: reservation countdown timer, upsell add-to-cart and the discount code field.
   All are custom elements so they survive the drawer's innerHTML re-renders. */
(function () {
  const TIMER_KEY = 'cro:cart-timer-deadline';
  const readDeadline = () => {
    try {
      return parseInt(sessionStorage.getItem(TIMER_KEY), 10);
    } catch (error) {
      return NaN;
    }
  };
  const saveDeadline = (deadline) => {
    try {
      sessionStorage.setItem(TIMER_KEY, String(deadline));
    } catch (error) {
      // Storage blocked: the countdown still runs for this page view.
    }
  };

  class CroCartTimer extends HTMLElement {
    connectedCallback() {
      this.minutes = parseInt(this.dataset.minutes, 10) || 10;
      this.display = this.querySelector('[data-cro-timer-display]');
      if (!this.display) return;

      // One window per tab, kept across pages and drawer re-renders; an expired window starts over on the next render.
      let deadline = readDeadline();
      if (!deadline || deadline <= Date.now()) {
        deadline = Date.now() + this.minutes * 60 * 1000;
        saveDeadline(deadline);
      }
      this.deadline = deadline;

      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }

    tick() {
      const remaining = Math.max(0, this.deadline - Date.now());
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      this.display.textContent = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      if (remaining <= 0) {
        // Hide the bar at zero rather than jumping back to the full time while the shopper watches.
        clearInterval(this.interval);
        this.hidden = true;
      }
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }
  }

  class CroCartUpsell extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-cro-upsell-add]');
      if (!this.button) return;
      this.button.addEventListener('click', () => this.addToCart());
    }

    async addToCart() {
      const variantId = this.button.dataset.variantId;
      const cartDrawer = document.querySelector('cart-drawer');
      if (!variantId || !cartDrawer) return;

      this.button.setAttribute('disabled', '');
      this.button.classList.add('loading');

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: cartDrawer.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        });
        const parsedState = await response.json();
        if (parsedState.status) throw new Error(parsedState.description || 'Add to cart failed');
        cartDrawer.renderContents(parsedState);
      } catch (error) {
        console.error('[cro-cart-upsell]', error);
        this.button.removeAttribute('disabled');
        this.button.classList.remove('loading');
      }
    }
  }

  // Applies codes through the Cart API, so they show in the drawer totals and carry through to checkout.
  class CroCartDiscount extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="discount"]');
      this.button = this.querySelector('.cro-cart-discount__apply');
      this.message = this.querySelector('.cro-cart-discount__message');
      if (!this.input || !this.button) return;

      this.querySelector('form').addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply();
      });
      this.input.addEventListener('input', () => {
        this.toggleReady();
        this.showMessage('');
      });
      this.querySelectorAll('[data-cro-discount-remove]').forEach((button) => {
        button.addEventListener('click', () => this.remove(button.closest('[data-code]').dataset.code));
      });
      this.toggleReady();
    }

    appliedCodes() {
      return [...this.querySelectorAll('[data-code]')].map((element) => element.dataset.code);
    }

    toggleReady() {
      this.classList.toggle('is-ready', this.input.value.trim() !== '');
    }

    showMessage(text, isError = false) {
      this.message.textContent = text;
      this.message.classList.toggle('is-error', Boolean(text) && isError);
      if (text && isError) this.input.setAttribute('aria-invalid', 'true');
      else this.input.removeAttribute('aria-invalid');
    }

    setBusy(busy) {
      this.busy = busy;
      this.classList.toggle('is-busy', busy);
      this.button.setAttribute('aria-busy', String(busy));
      this.input.readOnly = busy;
    }

    async update(codes, withSections) {
      const cartDrawer = document.querySelector('cart-drawer');
      const body = { discount: codes.join(',') };
      if (withSections && cartDrawer) {
        body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const cart = await response.json();
      if (!response.ok || !Array.isArray(cart.discount_codes)) throw new Error(cart.description || 'Discount update failed');
      return cart;
    }

    async apply() {
      if (this.busy) return;
      const code = this.input.value.trim();
      if (!code) {
        this.input.focus();
        return;
      }
      const applied = this.appliedCodes();
      if (applied.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
        this.input.value = '';
        this.toggleReady();
        return;
      }

      this.setBusy(true);
      this.showMessage('');
      try {
        const cart = await this.update([...applied, code], true);
        const result = cart.discount_codes.find((entry) => entry.code.toLowerCase() === code.toLowerCase());
        // Take rejected codes back off the cart so checkout doesn't receive them either.
        const accepted = cart.discount_codes.filter((entry) => entry.applicable).map((entry) => entry.code);
        if (accepted.length !== cart.discount_codes.length) await this.update(accepted, false);

        if (!result || !result.applicable) {
          this.setBusy(false);
          this.showMessage(this.dataset.error, true);
          return;
        }
        this.render(cart, code);
      } catch (error) {
        console.error('[cro-cart-discount]', error);
        this.setBusy(false);
        this.showMessage(window.cartStrings?.error || this.dataset.error, true);
      }
    }

    async remove(code) {
      if (this.busy) return;
      this.setBusy(true);
      this.showMessage('');
      try {
        const cart = await this.update(
          this.appliedCodes().filter((existing) => existing !== code),
          true
        );
        this.render(cart, null);
      } catch (error) {
        console.error('[cro-cart-discount]', error);
        this.setBusy(false);
        this.showMessage(window.cartStrings?.error || this.dataset.error, true);
      }
    }

    render(cart, appliedCode) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || !cart.sections) {
        window.location.reload();
        return;
      }
      cartDrawer.renderContents(cart);

      const field = cartDrawer.querySelector('cro-cart-discount');
      if (!field) return;
      const chip = appliedCode
        ? [...field.querySelectorAll('[data-code]')].find(
            (element) => element.dataset.code.toLowerCase() === appliedCode.toLowerCase()
          )
        : null;
      // An accepted code with nothing to take off the cart lines (a shipping discount) only shows at checkout.
      if (appliedCode && !chip) field.showMessage(field.dataset.checkoutNote);
      // Focus a button, not the input, so phones don't reopen the keyboard after the update.
      const target = chip ? chip.querySelector('[data-cro-discount-remove]') : field.button;
      if (typeof trapFocus === 'function') trapFocus(document.getElementById('CartDrawer'), target);
      else target.focus();
    }
  }

  if (!customElements.get('cro-cart-timer')) customElements.define('cro-cart-timer', CroCartTimer);
  if (!customElements.get('cro-cart-upsell')) customElements.define('cro-cart-upsell', CroCartUpsell);
  if (!customElements.get('cro-cart-discount')) customElements.define('cro-cart-discount', CroCartDiscount);
})();
